import { ACTIONS, isActionKind } from "../../../shared/goals/actions";
import { CHANNEL_ORDER, CHANNELS } from "../../../shared/goals/channels";
import type { GoalConfig } from "../../../shared/goals/config";
import type { Plan } from "../../../shared/goals/plan";
import { MODE, PLAN_PARTS } from "../goal/FullPlan";
import type { Block, Doc } from "../library/parts";
import { toolName } from "../library/tools";
import { settingLines } from "./Cards";

/** The plan fields each part shows, so a part is marked changed only when what it shows changed. */
export const PART_FIELDS: Record<string, (keyof Plan)[]> = {
  short: ["summary", "bet", "choices", "budget"],
  goal: ["goal"],
  found: ["findings"],
  lessons: ["lessons_used"],
  who: ["audience"],
  angles: ["angles"],
  tests: ["tests"],
  money: ["budget"],
  time: ["timeline"],
  run: ["autonomy"],
  risks: ["risks", "change_course_if"],
  asks: ["needs_from_you"],
  pbk: ["playbook_followed", "conflicts"],
};

const block = (text: string): Block => ({ t: "p", lvl: 0, text, from: 0, to: 0 });
const KIND = { skill: "Skill", method: "Tool", playbook: "Playbook" } as const;
const words = (name: string) => name.replace(/-/g, " ");
const names = (xs: unknown) => (Array.isArray(xs) ? xs.map((x) => (x as { name?: string }).name).filter(Boolean) : []);

/** Who sees the ads, in a line: the places, the ages and who Meta starts with. */
function audienceLine(a: unknown): string {
  const x = (a ?? {}) as { places?: { include?: unknown }; ages?: { min: number; max: number }; starts?: unknown };
  const bits = [names(x.places?.include).join(", "), x.ages ? `ages ${x.ages.min} to ${x.ages.max}` : "", names(x.starts).join(", ")].filter(Boolean);
  return bits.join("; ") || "Meta's own choice";
}

/** Every setting of a goal, one plain line each, in the words its settings cards use. */
function settings(c: GoalConfig): string[] {
  const meta = c.channels.meta?.settings ?? {};
  return [
    `The goal: ${c.sentence}`,
    ...settingLines(c),
    ...c.rules.never.map((n) => `Never: ${n}`),
    ...c.rules.own.map((n) => `Your rule: ${n}`),
    `Calling and texting: ${c.rules.callingAndTexting ? "on" : "off"}`,
    `Biggest raise of a daily budget: ${c.rules.stepUpPct}%`,
    ...CHANNEL_ORDER.flatMap((k) => {
      const ch = c.channels[k];
      if (!ch) return [];
      const name = CHANNELS[k].label;
      return [
        `${name}: ${ch.on ? "on" : "off"}`,
        ...ch.markets.map((m) => `${name} market: ${m}`),
        ...ch.rules.map((r) => `${name} rule: ${r}`),
        ...(ch.card ? [`${name}, on its own: ${ch.card.own}`, `${name}, asks you first: ${ch.card.ask}`] : []),
      ];
    }),
    ...(c.channels.meta
      ? [`Where the ads show: ${Array.isArray(meta.placements) ? `${meta.placements.length} spots chosen` : "Meta picks the spots"}`, `Who sees the ads: ${audienceLine(meta.audience)}`]
      : []),
    ...c.library.flatMap((p) => [`${KIND[p.kind]}: ${words(p.name)}, version ${p.version}`, ...Object.values(p.edits).map((t) => `Your words in ${words(p.name)}: ${t}`)]),
    ...Object.entries(c.tools).map(([t, on]) => `${toolName(t)}: ${on ? "on" : "off"}`),
    ...Object.entries(c.autonomy).map(([k, m]) => `${isActionKind(k) ? ACTIONS[k].label : words(k)}: ${MODE[m]}`),
  ];
}

const KEY: Record<string, string> = { budget_usd: "Budget", max_cost_per_result_usd: "Most per result", usd: "Amount" };
/** A plan field as lines: each list item one line, an object's values labelled by their field. */
function lines(v: unknown, key = ""): string[] {
  if (v === null || v === undefined || v === "") return [];
  if (typeof v === "number") return [key.endsWith("usd") ? `${KEY[key] ?? words(key.replace(/_usd$/, ""))}: $${v.toLocaleString("en-US")}` : key ? `${words(key)}: ${v}` : String(v)];
  if (typeof v !== "object") return [key && typeof v === "boolean" ? `${words(key)}: ${v ? "yes" : "no"}` : String(v)];
  if (Array.isArray(v)) return v.flatMap((x) => (x && typeof x === "object" && !Array.isArray(x) ? [Object.entries(x).flatMap(([k, y]) => lines(y, k)).join(" · ")] : lines(x)));
  return Object.entries(v).flatMap(([k, y]) => lines(y, k));
}

/** A version of a goal as parts to compare: its settings first, then the plan's parts as the plan page shows them. */
export function planDoc(config: GoalConfig, plan: Plan | null): Doc {
  const parts = [{ id: "settings", title: "Your settings", blocks: settings(config).map(block) }];
  if (plan) for (const [id, title] of PLAN_PARTS) parts.push({ id, title, blocks: (PART_FIELDS[id] ?? []).flatMap((k) => lines(plan[k])).map(block) });
  return { title: "The plan", rules: [], written: null, parts };
}
